import { useRef, useState } from "react";
import { ImageIcon, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { SecureImage } from "@/components/SecureImage";
import { api, formatApiErrorDetail } from "@/lib/api";

export function ProductImageEditor({ product, onUpdated, className = "" }) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const image = product.images?.[0];

  const uploadImage = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const response = await api.post(`/admin/products/${product.id}/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated?.(response.data);
      toast.success("Product image updated");
      setOpen(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Image upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const deleteImage = async () => {
    if (!image?.file_id) return;
    if (!window.confirm("Delete this product image? The product will remain available without an image.")) return;
    setDeleting(true);
    try {
      const response = await api.delete(`/admin/products/${product.id}/image/${image.file_id}`);
      onUpdated?.(response.data);
      toast.success("Product image deleted");
      setOpen(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Image delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" data-testid={`edit-image-button-${product.id}`} className={`${className} border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800`}>
          <ImageIcon className="h-4 w-4" /> Edit Image
        </Button>
      </DialogTrigger>
      <DialogContent className="border-slate-800 bg-slate-950 text-slate-100" data-testid={`product-image-editor-${product.id}`}>
        <DialogHeader>
          <DialogTitle className="font-headline text-2xl">Manage product image</DialogTitle>
          <DialogDescription className="text-slate-400">
            Upload a PNG or JPG. Manual uploads are stored in persistent object storage and synced to the published product.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
          {image?.file_id ? (
            <SecureImage fileId={image.file_id} alt={product.name} className="h-64 w-full object-contain" />
          ) : (
            <div className="flex h-64 flex-col items-center justify-center text-slate-500">
              <ImageIcon className="mb-2 h-10 w-10" /> No image attached
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button type="button" data-testid={`upload-product-image-button-${product.id}`} onClick={() => inputRef.current?.click()} disabled={uploading || deleting} className="bg-blue-500 text-white hover:bg-blue-400">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : image?.file_id ? "Replace image" : "Upload image"}
          </Button>
          <Button type="button" data-testid={`delete-product-image-button-${product.id}`} onClick={deleteImage} disabled={!image?.file_id || uploading || deleting} variant="outline" className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20">
            <Trash2 className="h-4 w-4" /> {deleting ? "Deleting..." : "Delete image"}
          </Button>
        </div>
        <input
          ref={inputRef}
          data-testid={`product-image-upload-input-${product.id}`}
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          className="hidden"
          onChange={(event) => uploadImage(event.target.files?.[0])}
        />
      </DialogContent>
    </Dialog>
  );
}
